import React, { ReactNode } from 'react';
import classNames from 'classnames';
import { X as CloseIcon } from 'lucide-react';

import { IconButton } from 'src/shared/components/ui/icon-button';

import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';

export type ModalProps = {
    onClose?: () => void;
    children: ReactNode;
    classNames?: {
        background?: string;
        body?: string;
    };
    dismissOnClickOutside?: boolean;
    mountInline?: boolean; // display modal inline instead of mounting to body
    /** Applied to the modal backdrop root (portal) for stable E2E selectors */
    dataTestId?: string;
};

export const InlineModal = (props: ModalProps) => (
    <Modal {...props} mountInline={true}>
        {props.children}
    </Modal>
);

const Modal = (props: ModalProps) => {
    const { dismissOnClickOutside = false, mountInline = false, dataTestId } = props;

    const preventEventBubbling = (event: React.SyntheticEvent) => {
        event.stopPropagation();
    };

    const backgroundClasses = classNames(mountInline ? styles.inlineBackground : styles.background, props.classNames?.background);

    const bodyClasses = classNames(styles.body, props.classNames?.body);

    const handleBackgroundClick = () => {
        if (dismissOnClickOutside && props.onClose) {
            props.onClose();
        }
    };

    const modal = (
        <div className={backgroundClasses} onClick={handleBackgroundClick} data-testid={dataTestId}>
            <div className={bodyClasses} onClick={preventEventBubbling}>
                {props.onClose && (
                    <div className={styles.close}>
                        <IconButton aria-label='close' onClick={props.onClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                )}
                <div className={styles.content}>{props.children}</div>
            </div>
        </div>
    );

    return mountInline ? modal : createPortal(modal, document.body);
};

export default Modal;
